import React, { Component } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { ModalStyled } from '../../../theme/customizedStyleComponents';
import { AgGridReact } from 'ag-grid-react';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { serviceConstants } from '../../../services/common/constants';
import { getUserAuthToken } from '../../../services/common/util';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class TestResultStatusRenderer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasResults: false,
            resultStatus: '',
            statusColor: '',
            pdfPath: '',
            showPopup: false,
            quickFilterText: '',
            rowData: [],
            abnormalCount: 0,
            inconsistentCount: 0,
            totalCount: 0,
            isQualitative: false,
            qualitativeValue: '',
            viewMode: 'grid', // 'grid' | 'sidebyside'
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true,
                resizable: true
            },
            columnDefs: [
                {
                    headerName: 'Analyte',
                    minWidth: 180,
                    field: 'analyteName'
                },
                {
                    headerName: 'Result',
                    minWidth: 90,
                    field: 'analyteResult',
                    cellStyle: (params) => {
                        const flag = params.data.analyteFlag;
                        if (flag === 'H' || flag === 'L' || flag === 'A') {
                            return { color: '#dc3545', fontWeight: 'bold' };
                        }
                        return null;
                    }
                },
                {
                    headerName: 'Units',
                    minWidth: 80,
                    field: 'analyteUnits'
                },
                {
                    headerName: 'Ref Range',
                    minWidth: 120,
                    field: 'analyteReferenceRange'
                },
                {
                    headerName: 'Flag',
                    minWidth: 75,
                    field: 'analyteFlag',
                    cellStyle: (params) => {
                        const flagColor = params.data.flagColor;
                        if (flagColor) {
                            return { color: `#${flagColor}`, fontWeight: 'bold' };
                        }
                        const flag = params.value;
                        if (flag === 'H') return { color: '#dc3545', fontWeight: 'bold' };
                        if (flag === 'L') return { color: '#0066cc', fontWeight: 'bold' };
                        if (flag === 'A') return { color: '#ff6600', fontWeight: 'bold' };
                        return null;
                    },
                    valueFormatter: (params) => {
                        const flag = params.value;
                        if (flag && !['H', 'L', 'A'].includes(flag)) return flag;
                        if (flag === 'H') return 'High';
                        if (flag === 'L') return 'Low';
                        if (flag === 'A') return 'Abnormal';
                        return flag || '';
                    }
                },
                {
                    headerName: 'Outcome',
                    minWidth: 110,
                    field: 'flagOutcome',
                    cellStyle: (params) => {
                        const outcome = params.value;
                        if (outcome === 'Consistent') return { color: '#28a745', fontWeight: 'bold' };
                        if (outcome === 'Inconsistent') return { color: '#dc3545', fontWeight: 'bold' };
                        return null;
                    },
                    valueFormatter: (params) => params.value || ''
                },
                {
                    headerName: 'Detection Window',
                    minWidth: 160,
                    field: 'detectionWindow',
                    tooltipField: 'detectionWindow',
                    cellStyle: { color: '#555', fontSize: '0.85em' },
                    valueFormatter: (params) => params.value || '—'
                }
            ],
            paginationNumberFormatter: function (params) {
                return '[' + params.value.toLocaleString() + ']';
            }
        };
    }

    componentDidMount() {
        this.updateResultStatus();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            this.updateResultStatus();
        }
    }

    updateResultStatus = () => {
        const { data } = this.props;

        // Root orders: aggregate results from child orderIds
        if (data && data.order_type === 'root' && Array.isArray(data.orderIds) && data.orderIds.length > 0) {
            const childResults = data.orderIds.filter(o => o && o.results && (o.results.value || (o.results.values && o.results.values.length > 0)));
            if (childResults.length > 0) {
                const allValues = [];
                childResults.forEach(o => {
                    if (o.results.values && o.results.values.length > 0) allValues.push(...o.results.values);
                    else if (o.results.value) allValues.push({ analyteName: o.test_id?.name || 'Result', analyteResult: o.results.value, analyteUnits: '', analyteReferenceRange: '', analyteFlag: '' });
                });
                const totalCount = allValues.length;
                const abnormalCount = allValues.filter(v => v.analyteFlag && ['H','L','A'].includes(v.analyteFlag)).length;
                const inconsistentCount = allValues.filter(v => v.flagOutcome === 'Inconsistent').length;
                const flaggedCount = allValues.filter(v => v.analyteFlag).length;
                let statusText = totalCount > 0 ? `${totalCount} analyte${totalCount !== 1 ? 's' : ''} complete` : 'Resulted';
                let statusColor = '#28a745';
                if (inconsistentCount > 0) { statusText = `Complete — ${inconsistentCount} inconsistent`; statusColor = '#dc3545'; }
                else if (abnormalCount > 0) { statusText = `Complete — ${abnormalCount} abnormal`; statusColor = '#dc3545'; }
                else if (flaggedCount > 0) { statusText = `Complete — ${flaggedCount} flagged`; statusColor = '#ffc107'; }
                this.setState({ hasResults: true, resultStatus: statusText, statusColor, pdfPath: '', rowData: allValues, abnormalCount, inconsistentCount, totalCount, isQualitative: false });
                return;
            }
            this.setState({ hasResults: false, resultStatus: 'Pending', statusColor: '#666', pdfPath: '', rowData: [], isQualitative: false });
            return;
        }

        if (!data || !data.results) {
            this.setState({
                hasResults: false,
                resultStatus: 'Pending',
                statusColor: '#666',
                pdfPath: '',
                rowData: [],
                isQualitative: false
            });
            return;
        }

        if (data.is_cancel) {
            this.setState({
                hasResults: false,
                resultStatus: 'Cancelled',
                statusColor: '#dc3545',
                pdfPath: '',
                rowData: [],
                isQualitative: false
            });
            return;
        }

        let pdfPath = '';
        if (data.results.pdf_path) {
            pdfPath = `${serviceConstants.API_HOST_NAME}${data.results.pdf_path}?${new Date().getTime()}&token=${getUserAuthToken()}`;
        }

        // Qualitative test (single result value — POC screen)
        if (data.results.value) {
            const value = data.results.value.toLowerCase();
            let statusColor = '#28a745';
            if (value.includes('positive') || value.includes('detected')) {
                statusColor = '#dc3545';
            } else if (value.includes('invalid') || value.includes('inconclusive')) {
                statusColor = '#ffc107';
            }

            this.setState({
                hasResults: true,
                resultStatus: data.results.value,
                statusColor,
                pdfPath,
                isQualitative: true,
                qualitativeValue: data.results.value,
                rowData: [
                    {
                        analyteName: data.test_id?.name || 'Test Result',
                        analyteResult: data.results.value,
                        analyteUnits: '',
                        analyteReferenceRange: '',
                        analyteFlag: '',
                        detectionWindow: ''
                    }
                ]
            });
            return;
        }

        // Quantitative test (LC-MS/MS confirmation — multiple analyte values)
        if (data.results.values && data.results.values.length > 0) {
            const totalCount = data.results.values.length;
            const flaggedCount = data.results.values.filter((v) => v.analyteFlag).length;
            const inconsistentCount = data.results.values.filter((v) => v.flagOutcome === 'Inconsistent').length;
            const abnormalCount = data.results.values.filter(
                (v) => v.analyteFlag && ['H', 'L', 'A'].includes(v.analyteFlag)
            ).length;

            let statusText = `${totalCount} analyte${totalCount > 1 ? 's' : ''} complete`;
            let statusColor = '#28a745';

            if (inconsistentCount > 0) {
                statusText = `Complete — ${inconsistentCount} inconsistent`;
                statusColor = '#dc3545';
            } else if (abnormalCount > 0) {
                statusText = `Complete — ${abnormalCount} abnormal`;
                statusColor = '#dc3545';
            } else if (flaggedCount > 0) {
                statusText = `Complete — ${flaggedCount} flagged`;
                statusColor = '#ffc107';
            }

            this.setState({
                hasResults: true,
                resultStatus: statusText,
                statusColor,
                pdfPath,
                rowData: data.results.values,
                abnormalCount,
                inconsistentCount,
                totalCount,
                isQualitative: false
            });
            return;
        }

        this.setState({
            hasResults: false,
            resultStatus: 'Pending',
            statusColor: '#666',
            pdfPath: '',
            rowData: [],
            isQualitative: false
        });
    };

    handleClose = () => this.setState({ showPopup: false });

    handleClick = () => {
        if (this.state.hasResults && this.state.rowData.length > 0) {
            this.setState({ showPopup: true });
        }
    };

    /**
     * Build medication reconciliation data.
     * Patient meds (medicationIds) vs analytes that triggered a flag.
     * Returns array of { medicationName, detected: bool, analyteNames: [] }
     */
    getMedicationReconciliation = () => {
        const { data } = this.props;
        if (!data || !data.medicationIds || !data.results || !data.results.values) return [];

        const patientMeds = data.medicationIds || [];
        const resultValues = data.results.values || [];

        // For each patient medication, check if any result value flags it as Consistent
        return patientMeds.map((med) => {
            const medName = med.name || med.medication_name || (typeof med === 'string' ? med : 'Unknown');
            // Find analytes that were detected (flagged) and are consistent with this med
            const matchingDetected = resultValues.filter(
                (v) => v.flagOutcome === 'Consistent' && v.analyteFlag
            );
            // An analyte is "consistent" with this med if it was flagged consistent
            // (The flag engine already did the med-match — we surface the result here)
            return {
                medName,
                medId: med._id
            };
        });
    };

    /**
     * Summarize detected vs. expected analytes for medication reconciliation.
     * Detected: has a flag (DET, H, L, A, etc.)
     * Expected: no flag or flagOutcome = Consistent (within expected range for patient's meds)
     * Unexpected: flagOutcome = Inconsistent
     */
    getDetectedAnalytes = () => {
        const { data } = this.props;
        if (!data || !data.results || !data.results.values) return { detected: [], notDetected: [] };

        const values = data.results.values;
        const detected = values.filter((v) => v.analyteFlag && v.analyteFlag !== '');
        const notDetected = values.filter((v) => !v.analyteFlag || v.analyteFlag === '');

        return { detected, notDetected };
    };

    renderMedicationReconciliation = () => {
        const { data } = this.props;
        if (!data || !data.results || !data.results.values) return null;

        const values = data.results.values;
        const patientMeds = (data.medicationIds || []).map(
            (m) => m.name || m.medication_name || (typeof m === 'string' ? m : null)
        ).filter(Boolean);

        // Split result values by outcome
        const inconsistentAnalytes = values.filter((v) => v.flagOutcome === 'Inconsistent');
        const consistentAnalytes = values.filter((v) => v.flagOutcome === 'Consistent');
        const notDetected = values.filter((v) => !v.analyteFlag || v.analyteFlag === '');

        const hasReconciliationData =
            patientMeds.length > 0 ||
            inconsistentAnalytes.length > 0 ||
            consistentAnalytes.length > 0;

        if (!hasReconciliationData) return null;

        return (
            <div
                style={{
                    marginTop: '16px',
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: '6px',
                    overflow: 'hidden'
                }}
            >
                <div
                    style={{
                        background: 'rgba(0,0,0,0.05)',
                        padding: '8px 14px',
                        fontWeight: '600',
                        fontSize: '0.9em',
                        borderBottom: '1px solid rgba(0,0,0,0.1)'
                    }}
                >
                    🔍 Medication Reconciliation
                </div>
                <div style={{ padding: '12px 14px' }}>
                    {/* Patient Medications */}
                    {patientMeds.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.82em', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '5px', opacity: 0.7 }}>
                                Patient Reported Medications
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {patientMeds.map((med, i) => (
                                    <Badge
                                        key={i}
                                        style={{
                                            background: '#0d6efd',
                                            color: '#fff',
                                            fontWeight: '500',
                                            fontSize: '0.8em',
                                            padding: '4px 9px',
                                            borderRadius: '20px'
                                        }}
                                    >
                                        💊 {med}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {/* Inconsistent results — unexpected findings */}
                        {inconsistentAnalytes.length > 0 && (
                            <div style={{ flex: '1', minWidth: '180px' }}>
                                <div style={{ fontWeight: '600', fontSize: '0.82em', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#dc3545', marginBottom: '5px' }}>
                                    ⚠ Inconsistent ({inconsistentAnalytes.length})
                                </div>
                                {inconsistentAnalytes.map((v, i) => (
                                    <div key={i} style={{ fontSize: '0.85em', marginBottom: '3px' }}>
                                        <span style={{ color: '#dc3545', fontWeight: '600' }}>{v.analyteName}</span>
                                        <span style={{ opacity: 0.7, marginLeft: '6px' }}>
                                            {v.analyteResult} {v.analyteUnits}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Consistent results — expected for patient's meds */}
                        {consistentAnalytes.length > 0 && (
                            <div style={{ flex: '1', minWidth: '180px' }}>
                                <div style={{ fontWeight: '600', fontSize: '0.82em', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#28a745', marginBottom: '5px' }}>
                                    ✓ Consistent ({consistentAnalytes.length})
                                </div>
                                {consistentAnalytes.map((v, i) => (
                                    <div key={i} style={{ fontSize: '0.85em', marginBottom: '3px' }}>
                                        <span style={{ color: '#28a745', fontWeight: '600' }}>{v.analyteName}</span>
                                        <span style={{ opacity: 0.7, marginLeft: '6px' }}>
                                            {v.analyteResult} {v.analyteUnits}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Not detected */}
                        {notDetected.length > 0 && (
                            <div style={{ flex: '1', minWidth: '180px' }}>
                                <div style={{ fontWeight: '600', fontSize: '0.82em', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.6, marginBottom: '5px' }}>
                                    Not Detected ({notDetected.length})
                                </div>
                                {notDetected.map((v, i) => (
                                    <div key={i} style={{ fontSize: '0.85em', marginBottom: '3px', opacity: 0.6 }}>
                                        {v.analyteName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    renderQualitativeBanner = () => {
        const { qualitativeValue } = this.state;
        if (!qualitativeValue) return null;

        const val = qualitativeValue.toLowerCase();
        let bgColor = '#d4edda';
        let textColor = '#155724';
        let icon = '✓';
        let label = 'NEGATIVE';

        if (val.includes('positive') || val.includes('detected')) {
            bgColor = '#f8d7da';
            textColor = '#721c24';
            icon = '⚠';
            label = 'POSITIVE';
        } else if (val.includes('invalid') || val.includes('inconclusive')) {
            bgColor = '#fff3cd';
            textColor = '#856404';
            icon = '?';
            label = 'INCONCLUSIVE';
        }

        return (
            <div
                style={{
                    background: bgColor,
                    color: textColor,
                    borderRadius: '8px',
                    padding: '16px 20px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                }}
            >
                <div style={{ fontSize: '2em', lineHeight: 1 }}>{icon}</div>
                <div>
                    <div style={{ fontWeight: '700', fontSize: '1.1em', letterSpacing: '0.05em' }}>{label}</div>
                    <div style={{ fontSize: '0.9em', opacity: 0.8, marginTop: '2px' }}>{qualitativeValue}</div>
                </div>
            </div>
        );
    };

    /**
     * Build side-by-side POC screen vs LC-MS/MS confirmation view.
     * Groups analytes by name, showing screening result alongside confirmation.
     */
    renderSideBySide = () => {
        const { data } = this.props;
        if (!data || !data.results || !data.results.values) return null;

        const values = data.results.values;

        // Separate screening (POC) from confirmation (LC-MS/MS)
        const screeningRows = values.filter(v => (v.analyteMethod || '').toLowerCase() === 'screening');
        const confirmationRows = values.filter(v => (v.analyteMethod || '').toLowerCase() !== 'screening');

        // Build a map by substance name for matching
        const substanceMap = {};
        confirmationRows.forEach(v => {
            const key = (v.analyteName || '').toLowerCase();
            if (!substanceMap[key]) substanceMap[key] = [];
            substanceMap[key].push(v);
        });

        // All unique substance names across both types
        const allNames = [...new Set([
            ...screeningRows.map(v => v.analyteName),
            ...confirmationRows.map(v => v.analyteName)
        ])];

        const outcomeStyle = (outcome) => {
            if (outcome === 'Consistent') return { color: '#28a745', fontWeight: '700' };
            if (outcome === 'Inconsistent') return { color: '#dc3545', fontWeight: '700' };
            return {};
        };

        const flagStyle = (flag) => {
            if (!flag) return {};
            if (flag === 'H') return { color: '#dc3545', fontWeight: '700' };
            if (flag === 'L') return { color: '#0066cc', fontWeight: '700' };
            if (flag === 'A') return { color: '#ff6600', fontWeight: '700' };
            return { color: '#dc3545', fontWeight: '700' };
        };

        const resultCell = (v) => {
            if (!v) return <span style={{ color: '#aaa' }}>—</span>;
            return (
                <div>
                    <span style={flagStyle(v.analyteFlag)}>{v.analyteResult || '—'}</span>
                    {v.analyteUnits && <span style={{ fontSize: '0.8em', opacity: 0.6, marginLeft: '4px' }}>{v.analyteUnits}</span>}
                    {v.analyteFlag && <span style={{ ...flagStyle(v.analyteFlag), marginLeft: '6px', fontSize: '0.8em' }}>[{v.analyteFlag}]</span>}
                    {v.flagOutcome && <div style={{ ...outcomeStyle(v.flagOutcome), fontSize: '0.75em', marginTop: '2px' }}>{v.flagOutcome}</div>}
                    {v.detectionWindow && <div style={{ fontSize: '0.72em', color: '#666', marginTop: '1px' }}>🕐 {v.detectionWindow}</div>}
                </div>
            );
        };

        const thStyle = {
            background: 'rgba(0,0,0,0.06)',
            padding: '8px 12px',
            fontWeight: '600',
            fontSize: '0.82em',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: '2px solid rgba(0,0,0,0.1)',
            whiteSpace: 'nowrap'
        };
        const tdStyle = {
            padding: '8px 12px',
            borderBottom: '1px solid rgba(0,0,0,0.07)',
            verticalAlign: 'top',
            fontSize: '0.88em'
        };

        const hasBoth = screeningRows.length > 0 && confirmationRows.length > 0;

        return (
            <div style={{ overflowX: 'auto' }}>
                {!hasBoth && (
                    <div style={{ color: '#888', fontStyle: 'italic', padding: '12px 0', fontSize: '0.88em' }}>
                        {screeningRows.length > 0
                            ? 'Only POC screen results available — no LC-MS/MS confirmation results yet.'
                            : 'Only LC-MS/MS confirmation results available — no POC screen results.'}
                    </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: '25%' }}>Analyte</th>
                            <th style={{ ...thStyle, width: '30%', color: '#0d6efd' }}>🔬 POC Screen</th>
                            <th style={{ ...thStyle, width: '30%', color: '#6f42c1' }}>⚗ LC-MS/MS Confirmation</th>
                            <th style={{ ...thStyle, width: '15%' }}>Ref Range</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allNames.map((name, i) => {
                            const poc = screeningRows.find(v => v.analyteName === name);
                            const conf = (substanceMap[(name || '').toLowerCase()] || [])[0];
                            const rowInconsistent = (poc?.flagOutcome === 'Inconsistent') || (conf?.flagOutcome === 'Inconsistent');
                            return (
                                <tr key={i} style={rowInconsistent ? { background: 'rgba(220,53,69,0.05)' } : {}}>
                                    <td style={{ ...tdStyle, fontWeight: '600' }}>{name}</td>
                                    <td style={tdStyle}>{poc ? resultCell(poc) : <span style={{ color: '#aaa' }}>—</span>}</td>
                                    <td style={tdStyle}>{conf ? resultCell(conf) : <span style={{ color: '#aaa' }}>—</span>}</td>
                                    <td style={{ ...tdStyle, color: '#666', fontSize: '0.82em' }}>
                                        {conf?.analyteReferenceRange || poc?.analyteReferenceRange || '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    render() {
        const { hasResults, resultStatus, statusColor, pdfPath, abnormalCount, inconsistentCount, isQualitative } = this.state;
        const hasAbnormal = abnormalCount > 0;
        const hasInconsistent = inconsistentCount > 0;
        const isClickable = hasResults && this.state.rowData.length > 0;

        let requisitionNumber = '';
        if (this.props.data?.parent_id && this.props.data?.parent_id.parent_id) {
            requisitionNumber = this.props.data?.parent_id.parent_id.lab_order_id;
        } else {
            requisitionNumber = this.props.data?.parent_id?.lab_order_id;
        }

        const reportStatus = this.props.data?.report_status;
        const reportStatusColors = { draft: '#6c757d', preliminary: '#fd7e14', final: '#28a745' };
        const reportStatusColor = reportStatusColors[reportStatus] || '#6c757d';

        // Determine if side-by-side view is available
        const values = this.props.data?.results?.values || [];
        const hasScreening = values.some(v => (v.analyteMethod || '').toLowerCase() === 'screening');
        const hasConfirmation = values.some(v => (v.analyteMethod || '').toLowerCase() !== 'screening');
        const canShowSideBySide = !isQualitative && (hasScreening || hasConfirmation);
        const { viewMode } = this.state;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '100%' }}>
                <span style={{ color: statusColor, fontWeight: hasResults ? '500' : 'normal' }}>
                    {(hasInconsistent || hasAbnormal) && <span style={{ marginRight: '4px' }}>⚠️</span>}
                    {resultStatus}
                </span>
                {isClickable && (
                    <button
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        title="View detailed results"
                        onClick={this.handleClick}
                        style={{ lineHeight: 1, flexShrink: 0 }}
                    >
                        <i className="fas fa-chart-bar"></i>
                    </button>
                )}

                <ModalStyled
                    size="xl"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                    show={this.state.showPopup}
                    onHide={this.handleClose}
                    backdrop="static"
                >
                    <Modal.Header closeButton>
                        <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <span>Test Results — {this.props.data?.test_id?.name || 'Unknown Test'}</span>
                            {reportStatus && (
                                <span
                                    style={{
                                        fontSize: '0.65em',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        color: reportStatusColor,
                                        border: `1px solid ${reportStatusColor}`,
                                        borderRadius: '4px',
                                        padding: '2px 8px',
                                        verticalAlign: 'middle'
                                    }}
                                >
                                    {reportStatus}
                                </span>
                            )}
                            {canShowSideBySide && (
                                <div style={{ display: 'flex', gap: '4px', fontSize: '0.6em' }}>
                                    <button
                                        onClick={() => this.setState({ viewMode: 'grid' })}
                                        style={{
                                            padding: '3px 10px',
                                            borderRadius: '4px 0 0 4px',
                                            border: '1px solid #0d6efd',
                                            background: viewMode === 'grid' ? '#0d6efd' : '#fff',
                                            color: viewMode === 'grid' ? '#fff' : '#0d6efd',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Grid
                                    </button>
                                    <button
                                        onClick={() => this.setState({ viewMode: 'sidebyside' })}
                                        style={{
                                            padding: '3px 10px',
                                            borderRadius: '0 4px 4px 0',
                                            border: '1px solid #0d6efd',
                                            borderLeft: 'none',
                                            background: viewMode === 'sidebyside' ? '#0d6efd' : '#fff',
                                            color: viewMode === 'sidebyside' ? '#fff' : '#0d6efd',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        POC vs LC-MS/MS
                                    </button>
                                </div>
                            )}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row form-row">
                            {/* Header — patient / sample info */}
                            <div className="col-12 mb-3">
                                <div className="row">
                                    <div className="col-md-4">
                                        <strong>Patient:</strong>{' '}
                                        <span
                                            style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                            title="View Patient Record"
                                            onClick={() => { window.location.href = '/clinic/patientMedications/' + this.props.data?.patient_id?._id; }}
                                        >
                                            {this.props.data?.patient_id?.first_name}{' '}
                                            {this.props.data?.patient_id?.last_name}
                                        </span>
                                    </div>
                                    <div className="col-md-4">
                                        <strong>Sample ID:</strong>{' '}
                                        <span
                                            style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                            title="Click to copy"
                                            onClick={() => { navigator.clipboard && navigator.clipboard.writeText(this.props.data?.external_sample_id || this.props.data?.test_info?.sample || ''); }}
                                        >
                                            {this.props.data?.external_sample_id || this.props.data?.test_info?.sample || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="col-md-4">
                                        <strong>Requisition:</strong>{' '}
                                        <span
                                            style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                            title="Go to Orders"
                                            onClick={() => { window.location.href = '/clinic/orders?req=' + encodeURIComponent(requisitionNumber || ''); }}
                                        >
                                            {requisitionNumber || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-4">
                                        <strong>Status:</strong>{' '}
                                        <span style={{ color: statusColor }}>{resultStatus}</span>
                                    </div>
                                    <div className="col-md-4">
                                        <strong>Order Status:</strong>{' '}
                                        {this.props.data?.status || 'N/A'}
                                    </div>
                                    <div className="col-md-4">
                                        {this.state.totalCount > 0 && (
                                            <span>
                                                <strong>Summary:</strong>{' '}
                                                {this.state.totalCount} analytes
                                                {hasInconsistent && ` · ${inconsistentCount} inconsistent`}
                                                {!hasInconsistent && hasAbnormal && ` · ${abnormalCount} abnormal`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* POC screen qualitative result banner */}
                            {isQualitative && (
                                <div className="col-12">
                                    {this.renderQualitativeBanner()}
                                </div>
                            )}

                            {/* Results — Grid or Side-by-Side view */}
                            {viewMode === 'sidebyside' ? (
                                <div className="col-12" style={{ marginBottom: '12px' }}>
                                    {this.renderSideBySide()}
                                    <div className="col-12 mt-3">
                                        {this.renderMedicationReconciliation()}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <ThemeContext.Consumer>
                                        {({ themeName }) => (
                                            <div
                                                id="analyteResultsGrid"
                                                style={{ height: '380px', width: '100%', marginBottom: '12px' }}
                                            >
                                                <AgGridReact
                                                    columnDefs={this.state.columnDefs}
                                                    defaultColDef={this.state.defaultColDef}
                                                    rowData={this.state.rowData}
                                                    pagination={true}
                                                    paginationPageSize={20}
                                                    paginationNumberFormatter={this.state.paginationNumberFormatter}
                                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                                    quickFilterText={this.state.quickFilterText}
                                                    tooltipShowDelay={300}
                                                />
                                            </div>
                                        )}
                                    </ThemeContext.Consumer>

                                    {/* Medication Reconciliation panel */}
                                    {!isQualitative && (
                                        <div className="col-12">
                                            {this.renderMedicationReconciliation()}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Footer actions */}
                            <div
                                className="col-12"
                                style={{ paddingTop: '14px', borderTop: '1px solid rgba(0,0,0,0.12)', marginTop: '12px' }}
                            >
                                {pdfPath && (
                                    <Button
                                        variant="primary"
                                        style={{ float: 'left' }}
                                        onClick={() => window.open(pdfPath, '_blank')}
                                    >
                                        <i className="fa fa-file-pdf-o"></i> View PDF
                                    </Button>
                                )}
                                <Button variant="secondary" style={{ float: 'right' }} onClick={this.handleClose}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </Modal.Body>
                </ModalStyled>
            </div>
        );
    }
}
